<template>
	<div 
		v-if="displayRow" 
		class="kanban-task-card"
		:class="{
			'priority-top': task.priority === 'top',
			'priority-high': task.priority === 'high',
			'priority-medium': task.priority === 'medium',
			'priority-low': task.priority === 'low',
			'priority-negative': task.priority === 'negative',
			'task-completed': task.completed === 'Y',
			'task-overdue': isOverdue
		}"
		draggable="true" 
		@dragstart="drag($event, task)" 
		@dragover="dragOver($event, task)"
		:id="task.id"
	>
		<!-- Card Header -->
		<div class="kanban-card-header">
			<div class="kanban-card-title-row">
				<b-icon
					:icon="task.completed === 'Y' ? 'check-circle-fill' : 'circle'"
					:variant="task.completed === 'Y' ? 'success' : 'secondary'"
					@click="toggleTaskFlag('completed')"
					style="cursor: pointer; width: 20px; height: 20px; flex-shrink: 0;"
				/>
				
				<b-link
					:to="{ name: 'task-detail', params: { taskId: localTask._id } }"
					target="_blank"
					class="kanban-card-title"
				>
					<b-icon-star-fill v-if="task.star === 'Y'" variant="warning" style="margin-right: 4px;" />
					{{ task.title }}
				</b-link>
				
				<b-icon-star
					v-if="task.star === 'N'"
					:id="'kanban-star-' + task.id"
					variant="secondary"
					@click="toggleTaskFlag('star')"
					style="cursor: pointer; width: 18px; height: 18px; flex-shrink: 0; opacity: 0.4;"
				/>
			</div>
		</div>

		<!-- Card Body -->
		<div class="kanban-card-body">
			<!-- Metadata Badges -->
			<div class="kanban-card-meta">
				<span v-if="task.status && groupField !== 'status'" class="kanban-badge badge-status">
					<b-icon icon="circle-fill" style="width: 12px; height: 12px;" />
					{{ statusName }}
				</span>
				
				<span v-if="task.priority && groupField !== 'priority'" class="kanban-badge" :class="'badge-priority-' + task.priority">
					<b-icon 
						:icon="priorityIcon" 
						style="width: 12px; height: 12px;"
					/>
					{{ priorityLabel }}
				</span>
				
				<!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Apply tinted badge style when a folder color is assigned -->
				<span v-if="task.folder && groupField !== 'folder'" class="kanban-badge badge-folder" :style="folderBadgeStyle">
					<b-icon icon="folder" style="width: 12px; height: 12px;" />
					{{ folderName }}
				</span>

				<!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Apply tinted badge style when a context color is assigned -->
				<span v-if="task.context && groupField !== 'context'" class="kanban-badge badge-context" :style="contextBadgeStyle">
					<b-icon icon="tag" style="width: 12px; height: 12px;" />
					{{ contextName }}
				</span>
				
				<span v-if="childCount > 0" class="kanban-badge">
					<b-icon icon="diagram-3" style="width: 12px; height: 12px;" />
					{{ childCount }}
				</span>
			</div>

			<!-- Dates -->
			<div class="kanban-card-dates">
				<div v-if="task.start || task.due" class="kanban-date-row">
					<div v-if="task.start" class="kanban-date-item">
						<b-icon icon="play-circle" variant="secondary" style="width: 14px; height: 14px;" />
						<span class="kanban-date-value">{{ convertMillisForDisplay(task.start, 'MM/DD/YYYY') }}</span>
						<span v-if="task.starttime" class="kanban-time-value">{{ convertMillisForDisplay(task.starttime, 'h:mm a') }}</span>
					</div>
					
					<span v-if="task.start && task.due" class="kanban-date-separator">→</span>
					
					<div v-if="task.due" class="kanban-date-item">
						<b-icon 
							icon="calendar-check" 
							:variant="isOverdue ? 'danger' : isDueToday ? 'warning' : 'secondary'" 
							style="width: 14px; height: 14px;"
						/>
						<span class="kanban-date-value" :class="{ 'text-danger': isOverdue, 'text-warning': isDueToday }">
							{{ dueDateDisplay }}
						</span>
						<span v-if="task.duetime" class="kanban-time-value">{{ convertMillisForDisplay(task.duetime, 'h:mm a') }}</span>
					</div>
				</div>
			</div>

			<!-- Notes Preview -->
			<div v-if="task.notes" class="kanban-card-notes">
				<vue-markdown :source="task.notes" />
			</div>
		</div>
	</div>
</template>

<script>
import TaskService from '@/services/TaskService'
import VueMarkdown from 'vue-markdown'

import taskOps from '@/mixins/taskOps'
import genericOps from '@/mixins/genericOps'

export default {
	name: 'TaskCard',
	components: {
		'vue-markdown': VueMarkdown,
	},
	mixins: [taskOps, genericOps],
	props: {
		advancedFilter: {
			type: Object,
			default: {},
		},
		
		childrenExpanded: {
			type: Boolean,
			default: false,
		},
		columnIndex: {
			type: Number,
			default: 0,
		},
		detailExpanded: {
			type: Boolean,
			default: false,
		},
		groupField: {
			type: String,
			default: null,
		},
		rowIndex: {
			type: Number,
			default: 0,
		},
		stackRank: {
			type: Number,
			default: 0
		},
		task: {
			type: Object,
			default: {
				id: null,
				children: [],
			},
		},
	},
	data() {
		return {
			childFields: [
				{ key: '_id', label: '' },
				{ key: 'title_id', label: 'Title' },
				{ key: 'startstarttime', label: 'Start Date' },
				{ key: 'dueduetime', label: 'Due Date' },
				{ key: 'priority', label: 'Priority' },
				{ key: 'status', label: 'Status' },
			],
			dateFormatOptions: {
				year: 'numeric',
				month: 'numeric',
				day: '2-digit',
				weekday: 'short',
			},
			hideChildren: this.childrenExpanded,
			hideChildrenIcon: 'chevron-compact-left',
			hideDetail: this.detailExpanded,
			hideDetailIcon: 'chevron-compact-right',
		}
	},
	computed: {
		childCount: function() {
			if (typeof this.task.children != 'undefined' && this.task.children !== null) {
				return this.task.children.length
			} else {
				return 0
			}
		},
		isOverdue: function() {
			if (!this.task.due) return false;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const dueDate = new Date(this.task.due);
			dueDate.setHours(0, 0, 0, 0);
			return dueDate < today && this.task.completed !== 'Y';
		},
		isDueToday: function() {
			if (!this.task.due) return false;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const dueDate = new Date(this.task.due);
			dueDate.setHours(0, 0, 0, 0);
			return dueDate.getTime() === today.getTime();
		},
		dueDateDisplay: function() {
			if (!this.task.due) return null;
			
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const dueDate = new Date(this.task.due);
			dueDate.setHours(0, 0, 0, 0);
			
			const tomorrow = new Date(today);
			tomorrow.setDate(tomorrow.getDate() + 1);
			
			if (dueDate < today) {
				return this.convertMillisForDisplay(this.task.due, 'MM/DD/YYYY');
			} else if (dueDate.getTime() === today.getTime()) {
				return 'Due Today';
			} else if (dueDate.getTime() === tomorrow.getTime()) {
				return 'Due Tomorrow';
			} else {
				return this.convertMillisForDisplay(this.task.due, 'MM/DD/YYYY');
			}
		},
		priorityIcon: function() {
			const icons = {
				top: 'chevron-double-up',
				high: 'arrow-up',
				medium: 'dash',
				low: 'arrow-down',
				negative: 'chevron-double-down'
			};
			return icons[this.task.priority] || 'dash';
		},
		priorityLabel: function() {
			const labels = {
				top: 'Top',
				high: 'High',
				medium: 'Medium',
				low: 'Low',
				negative: 'Negative'
			};
			return labels[this.task.priority] || this.task.priority;
		},
		statusName: function() {
			if (!this.task.status) return '';
			const statuses = this.$store.getters.getStatuses || [];
			const status = statuses.find(s => s._id === this.task.status);
			return status ? status.name : this.task.status;
		},
		folderName: function() {
			if (!this.task.folder) return '';
			const folders = this.$store.getters.getFoldersForProfile || [];
			const folder = folders.find(f => f._id === this.task.folder);
			return folder ? folder.name : this.task.folder;
		},
		contextName: function() {
			if (!this.task.context) return '';
			const contexts = this.$store.getters.getContexts || [];
			const context = contexts.find(c => c._id === this.task.context);
			return context ? context.name : this.task.context;
		},
		// 20260416 -- Copilot (Claude Sonnet 4.6) -- Dynamic badge style based on assigned context color
		contextBadgeStyle() {
			const color = this.$store.getters.getContextColorMap[this.task.context]
			if (!color) return {}
			return { backgroundColor: color + '1a', color }
		},
		// 20260416 -- Copilot (Claude Sonnet 4.6) -- Dynamic badge style based on assigned folder color
		folderBadgeStyle() {
			const color = this.$store.getters.getFolderColorMap[this.task.folder]
			if (!color) return {}
			return { backgroundColor: color + '1a', color }
		},
		displayRow: function() {
			// let display = false

			// //console.log('displayRow based on: ' + JSON.stringify(this.advancedFilter))

			// if (typeof this.advancedFilter != 'undefined' && this.advancedFilter !== null) {
			// 	Object.keys(this.advancedFilter).forEach((key, index) => {
			// 		//console.log(this.task.title + ': ' + this.task[key] + ' vs ' + this.advancedFilter[key])

			// 		if (
			// 			(typeof this.task[key] == 'undefined' || this.task[key] === null) &&
			// 			this.advancedFilter[key] == 'unassigned'
			// 		) {
			// 			display = true
			// 		} else if (
			// 			typeof this.task[key] != 'undefined' &&
			// 			this.task[key] !== null &&
			// 			this.task[key] == this.advancedFilter[key]
			// 		) {
			// 			display = true
			// 		} else {
			// 			display = false
			// 		}
			// 	})
			// }

			// return display

				 
	 	// Is this task a parent or a child?
        	if(this.parentTaskId() == 'N/A') {
        
          	// Task has no parent so it is a parent.
          	if(typeof this.$store.getters.getUserPrefs.taskSortOrder.hideParents != 'undefined'
          	&& this.$store.getters.getUserPrefs.taskSortOrder.hideParents !== null
          	&& this.$store.getters.getUserPrefs.taskSortOrder.hideParents) {
          
            		return false

          	} else {
            
            		return true
          
          	}

        	} else {

			// Task is a child.
			if(typeof this.$store.getters.getUserPrefs.taskSortOrder.hideChildren != 'undefined'
			&& this.$store.getters.getUserPrefs.taskSortOrder.hideChildren !== null
			&& this.$store.getters.getUserPrefs.taskSortOrder.hideChildren) {
			
				return false

			} else {
			
				return true
			
			}

		}
		
		},
		localTask: {
			get: function() {
				return this.task
			},
			set: function(newVal) {
				////console.log('TaskTableRow.localTask.set()')
				this.task = newVal
			},
		},
		parentTaskName: function() {
			if (
				typeof this.task.parentTask != 'undefined' &&
				this.task.parentTask !== null &&
				this.task.parentTask.length > 0
			) {
				return this.task.parentTask[0].title
			} else {
				return 'N/A'
			}
		},
	},
	watch: {
		hideChildren: function(newVal) {
			this.hideChildren = newVal

			if (this.hideChildren) {
				this.hideChildrenIcon = 'chevron-compact-down'
			} else {
				this.hideChildrenIcon = 'chevron-compact-left'
			}
		},
		hideDetail: function(newVal) {
			this.hideDetail = newVal

			if (this.hideDetail) {
				this.hideDetailIcon = 'chevron-compact-down'
			} else {
				this.hideDetailIcon = 'chevron-compact-right'
			}
		},
		childrenExpanded: function(newVal) {
			this.hideChildren = newVal

			if (this.hideChildren) {
				this.hideChildrenIcon = 'chevron-compact-down'
			} else {
				this.hideChildrenIcon = 'chevron-compact-left'
			}
		},
		detailExpanded: function(newVal) {
			this.hideDetail = newVal

			if (this.hideDetail) {
				this.hideDetailIcon = 'chevron-compact-down'
			} else {
				this.hideDetailIcon = 'chevron-compact-right'
			}
		},
	},
	mounted() {},
	methods: {
		convertMillisForDisplay(value, format) {
			// 20200321 -- ZWS -- Created because, apparently, the component
			// doesn't have access to TaskService from its template section

			return TaskService.getStringFromMillis(value, format)
		},
		drag(event, task) {
			this.$emit('drag:task', { columnIndex: this.columnIndex, rowIndex: this.rowIndex, event: event, task: task,  })
		},
		dragOver(event, task) {
			this.$emit('dragOver:task', { columnIndex: this.columnIndex, rowIndex: this.rowIndex, event: event, task: task,  })
		},
		parentTaskId() {
			if (
				typeof this.task.parentTask != 'undefined' &&
				this.task.parentTask !== null &&
				this.task.parentTask.length > 0
			) {
				return this.task.parentTask[0]._id
			} else {
				return 'N/A'
			}
		},
		emitCreateChildTask() {
			this.$emit('createChild:task', this.task)
		},
		emitDelete(id) {
			this.$emit('delete:task', id)
		},
		/*
        20200318 -- I hope this is gone.
        toggle(element, id) {
            
            ////////console.log('Toggling: ' + element + '-' + id)
            
            let ele = this.$refs[element + "-" + id]

            if(ele.classList.contains('invisible')) {
                ele.classList.replace("invisible", 'visible')	
                ele.classList.replace("d-none", 'd-inline')
            } else if(ele.classList.contains('visible')) {
                ele.classList.replace("visible", 'invisible')	
                ele.classList.replace("d-inline", 'd-none')	
            }


        },
        */
		toggleChildDetail() {
			// 20200318 -- Expand/collapse child tasks (aka sub tasks)
			this.hideChildren = !this.hideChildren
		},
		toggleDetail() {
			// 20200318 -- Expand/collapse task detail
			this.hideDetail = !this.hideDetail
		},
		// translateDateTime(date, time) {
		//     let value = TaskService.translateUnixDateForDisplay(date, time)
		//     return value
		// },
		// 20200318 -- Hopefully this isn't needed
		// translateRepeat(repeat) {

		//     let value ='';

		//     try {
		//         value = TaskService.translateiCalRRuleForDisplay(repeat)
		//     } catch(e) {
		//         //////console.log(e)
		//         value = repeat
		//     }

		//     return value;
		// },
	},
}
</script>

<style scoped>
.text-secondary font-weight-bold {
	color: grey;
	font-weight: 500;
}
</style>
